import React, { useState, ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Modal } from 'react-bootstrap'
import { DashboardIcon, UsersIcon, LogoutIcon } from '../../../assets/icons'
import useGameStore from '../../../store/GameStore'
import useUserStore from '../../../store/UserStore'
import { SOCKET_EVENTS } from '@chessx/shared/socket'
import LogoutComponent from '../../Login/LogoutComponent/LogoutComponent'
import './Sidebar.scss'

interface RouteItem {
  name: string
  route: string
  activeRoutes: string[]
  icon: ReactNode
}

const routes: RouteItem[] = [
  {
    name: 'Dashboard',
    route: '/admin/dashboard',
    activeRoutes: ['/admin/dashboard'],
    icon: (
      <span className="icon-fill">
        <DashboardIcon />
      </span>
    )
  },
  {
    name: 'Users',
    route: '/admin/users',
    activeRoutes: ['/admin/users', '/admin/users/detail'],
    icon: (
      <span className="icon-fill">
        <UsersIcon />
      </span>
    )
  },
  {
    name: 'Opening Tree Explorer',
    route: '/admin/opening-tree',
    activeRoutes: ['/admin/opening-tree'],
    icon: (
      <span className="icon-stroke">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      </span>
    )
  },
  {
    name: 'Heroes',
    route: '/admin/heroes',
    activeRoutes: ['/admin/heroes'],
    icon: (
      <span className="icon-stroke">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
      </span>
    )
  },
  {
    name: 'Chess Engine',
    route: '/admin/chess-apis',
    activeRoutes: ['/admin/chess-apis'],
    icon: (
      <span className="icon-stroke">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      </span>
    )
  },
  {
    name: 'Tournaments',
    route: '/admin/tournaments',
    activeRoutes: ['/admin/tournaments'],
    icon: (
      <span className="icon-stroke">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="8" r="6" />
          <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
        </svg>
      </span>
    )
  },
  {
    name: 'Puzzles',
    route: '/admin/puzzles',
    activeRoutes: ['/admin/puzzles'],
    icon: (
      <span className="icon-stroke">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19.439 7.85c-.049.322.059.648.289.878l1.568 1.568c.47.47.706 1.087.706 1.704s-.235 1.233-.706 1.704l-1.611 1.611a.98.98 0 0 1-.837.276c-.47-.07-.802-.48-.968-.925a2.501 2.501 0 1 0-3.214 3.214c.446.166.855.497.925.968a.979.979 0 0 1-.276.837l-1.61 1.61a2.404 2.404 0 0 1-1.705.707 2.402 2.402 0 0 1-1.704-.706l-1.568-1.568a1.026 1.026 0 0 0-.877-.29c-.493.074-.84.504-1.02.968a2.5 2.5 0 1 1-3.237-3.237c.464-.18.894-.527.967-1.02a1.026 1.026 0 0 0-.289-.877l-1.568-1.568A2.402 2.402 0 0 1 1.998 12c0-.617.236-1.234.706-1.704L4.23 8.77c.24-.24.581-.353.917-.303.515.077.877.528 1.073 1.01a2.5 2.5 0 1 0 3.259-3.259c-.482-.196-.933-.558-1.01-1.073-.05-.336.062-.676.303-.917l1.525-1.525A2.402 2.402 0 0 1 12 1.998c.617 0 1.234.236 1.704.706l1.568 1.568c.23.23.556.338.877.29.493-.074.84-.504 1.02-.968a2.5 2.5 0 1 1 3.237 3.237c-.464.18-.894.527-.967 1.02Z" />
        </svg>
      </span>
    )
  },
  {
    name: 'Reconciliation',
    route: '/admin/reconciliation',
    activeRoutes: ['/admin/reconciliation'],
    icon: (
      <span className="icon-stroke">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </span>
    )
  },
  {
    name: 'App Config',
    route: '/admin/app-config',
    activeRoutes: ['/admin/app-config'],
    icon: (
      <span className="icon-stroke">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </span>
    )
  }
]

const Sidebar = () => {
  const [logoutVisible, setLogoutVisible] = useState(false)
  const { socket } = useGameStore()
  const { setWalletAddress, setUser, setUserId, setToken } = useUserStore()
  const navigate = useNavigate()
  const location = useLocation()

  const onClickDisconnectUser = async () => {
    setUser(null)
    setWalletAddress(null)
    setLogoutVisible(false)
    setUserId(null)
    setToken(null)
    if (socket) {
      socket.emit(SOCKET_EVENTS.ADMIN_DISCONNECT_USER)
      socket.disconnect()
    }
    localStorage.clear()
    navigate('/login')
  }

  const isActive = (route: RouteItem) =>
    route.activeRoutes.some(
      activeRoute =>
        location.pathname === activeRoute || location.pathname.startsWith(`${activeRoute}/`)
    )

  return (
    <>
      <aside className="admin-sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-brand-icon">♛</div>
          <div className="sidebar-brand-text">
            <span className="brand-name">ChessX</span>
            <span className="brand-sub">Admin Panel</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <span className="sidebar-nav-label">Navigation</span>
          {routes.map(route => (
            <button
              key={route.name}
              className={`sidebar-nav-item ${isActive(route) ? 'active' : ''}`}
              onClick={() => navigate(route.route)}
            >
              <span className="nav-item-icon">{route.icon}</span>
              <span className="nav-item-name">{route.name}</span>
              {isActive(route) && <span className="nav-item-indicator" />}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button className="sidebar-logout-btn" onClick={() => setLogoutVisible(true)}>
            <LogoutIcon />
            <span>Log Out</span>
          </button>
        </div>
      </aside>

      <Modal centered show={logoutVisible} onHide={() => setLogoutVisible(false)}>
        <LogoutComponent
          onLogout={onClickDisconnectUser}
          onCancel={() => setLogoutVisible(false)}
          lightTheme={true}
        />
      </Modal>
    </>
  )
}

export default Sidebar
